import styled, { css } from 'styled-components'

import { COMPARE_TINT } from '../../compareTint'

export const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme.colors.border.A};
`

export const Header = styled.header`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px 8px;
`

export const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.C};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
`

export const Count = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.B};
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  font-variant-numeric: tabular-nums;
`

export const Message = styled.p`
  margin: 0;
  padding: 0 16px 16px;
  font-size: ${({ theme }) => theme.fontSizes.C};
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
`

export const List = styled.ul`
  margin: 0;
  padding: 0 8px 12px;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px; /* stylelint-disable-line scales/space */
  overflow-y: auto;
  max-height: 320px;
`

export const Row = styled.li`
  display: flex;
  flex-direction: column;
`

interface ChangeButtonProps {
  $selected?: boolean
  $nested?: boolean
  /**
   * The row is for something only the compared version has, so it takes that
   * version's colour when selected - the same pink the canvas marks it with.
   */
  $compared?: boolean
}

/**
 * The chevron column and the gap after it, which the entity row's button starts
 * past. A layer nested under it has to clear the same distance before its own
 * indent counts for anything, or the two rows line up and the nesting is lost.
 */
const DISCLOSURE_GUTTER = '18px'

export const ChangeButton = styled.button<ChangeButtonProps>`
  appearance: none;
  border: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;

  display: flex;
  align-items: flex-start;
  gap: 4px;

  padding: 6px 8px; /* stylelint-disable-line scales/space */
  padding-left: ${({ $nested }) =>
    $nested ? `calc(${DISCLOSURE_GUTTER} + 24px)` : '8px'};
  border-radius: 5px;

  background: transparent;
  transition: 100ms background linear;

  :hover,
  :focus-visible {
    background: ${({ theme }) => theme.colors.background.tertiary.C};
  }

  ${({ $selected, $compared, theme }) =>
    $selected &&
    css`
      background: ${$compared ? COMPARE_TINT.wash : theme.colors.sketch.F};

      :hover,
      :focus-visible {
        background: ${$compared ? COMPARE_TINT.wash : theme.colors.sketch.F};
      }
    `}
`

/**
 * Everything the row says, beside its icon rather than under it.
 *
 * The icon answers "what kind of thing is this" and the name answers "which
 * one", and those are read together - a Text layer called "Title" is one fact,
 * not two - so the icon belongs on the name's line. Putting it in a column of
 * its own rather than in the flow of the name also lets the second line start
 * under the name instead of under the icon, which is what makes a scan down
 * the list land on one left edge.
 */
export const Text = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px; /* stylelint-disable-line scales/space */
  /* Lets Name shrink far enough to ellipsis instead of pushing the row wide. */
  min-width: 0;
`

export const Name = styled.span<{ $selected?: boolean; $compared?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.C};
  color: ${({ theme, $selected, $compared }) => {
    if (!$selected) return theme.colors.foreground.secondary.A

    return $compared ? COMPARE_TINT.strong : theme.colors.sketch.A
  }};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

export const What = styled.span<{ $bulleted?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.B};
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* Only for one of several things a layer says: a lone bullet reads as a
  list with a piece missing. */
  ${({ $bulleted }) =>
    $bulleted &&
    css`
      ::before {
        content: '•';
        margin-right: 5px; /* stylelint-disable-line scales/space */
      }
    `}
`

/**
 * Names the group of changes that belong to no frame or Section.
 *
 * Not a button: there is nothing to select or travel to - the page is already
 * what the canvas is showing. It reads as a heading rather than as a row so the
 * layers beneath it are not mistaken for frames, which is what they would look
 * like listed flat among them.
 */
export const GroupLabel = styled.span`
  padding: 12px 8px 4px;
  font-size: ${({ theme }) => theme.fontSizes.B};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
`

/**
 * Holds the chevron's column open on a row with nothing to fold, so every name
 * in the list starts at the same place whether or not it has layers under it.
 */
export const DisclosureSpacer = styled.span`
  width: 16px;
  flex: 0 0 auto;
`

/**
 * The twist-down that shows or hides an entity's layers.
 *
 * Its own control rather than the whole row, because the row already does
 * something: it selects the entity and travels the canvas to it.
 */
export const Disclosure = styled.button<{ $open: boolean }>`
  appearance: none;
  border: 0;
  padding: 0;
  margin: 0;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  position: relative;
  /* Sits on the name's line rather than at the top of the block, matching the
     padding ChangeButton puts above its own first line. */
  margin-top: 6px; /* stylelint-disable-line scales/space */
  color: ${({ theme }) => theme.colors.foreground.secondary.D};

  /*
   * The arrow stays small - it is the quietest control in the row and should
   * not compete with the name beside it - but a 16px square is not a fair
   * thing to ask anyone to hit, least of all on a touchpad. The target reaches
   * past the arrow on every side into space the row is not using, so it grows
   * to 24px without the arrow growing with it and without anything moving.
   */
  ::after {
    content: '';
    position: absolute;
    inset: -4px;
  }

  svg {
    width: 10px;
    height: 10px;
    transition: transform ${({ theme }) => theme.transitions.duration[1]}
      ${({ theme }) => theme.transitions.timing.easeInOut};
    transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  }

  :hover {
    color: ${({ theme }) => theme.colors.foreground.secondary.A};
  }
`

/** Holds the twist-down and the row, so the row still fills the width. */
export const RowHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2px; /* stylelint-disable-line scales/space */
`

/**
 * The icon saying what a row is - a frame, a Section, a text layer.
 *
 * Aligned to the first line rather than centred: a row is two lines when it has
 * a summary under the name, and an icon floating between them belongs to
 * neither.
 */
export const RowIcon = styled.span<{
  $selected?: boolean
  $compared?: boolean
}>`
  display: flex;
  flex: 0 0 auto;
  /* Optically centres the 17px glyph on the name's line box. */
  margin-top: -1px; /* stylelint-disable-line scales/space */

  /* The glyph follows the name. ElementIcon colours its own container, so this
  has to outweigh it rather than be inherited. */
  ${({ $selected, $compared, theme }) =>
    $selected &&
    css`
      && > * {
        color: ${$compared ? COMPARE_TINT.strong : theme.colors.sketch.A};
      }
    `}
`
